import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
// Import Global Components
import { Loader, ErrorComponent } from 'shared-components';
import { get, post } from 'shared-components/src/utils/http';
// Import Global Hooks
import useAlert from '../../hooks/useAlert';
// Import Contexts
import { ProgramConfigContext } from '../../contexts/ProgramConfigContext';
// Import Global Components
import Heading from '../../components/Heading';
import Card from '../../components/Card';
import Alert from '../../components/Alert';
// Import Translations
import { profileEn } from '../../i18n/profile';
// Import Utils
import { getCurrentAccount } from '../../utils/accounts';
import {
  checkSelectedState,
  stateKeys,
  provinceKeys,
  subscriberDigits,
} from '../../utils/profile';
// Import Local Components
import CardInfo from './components/CardInfo';
import DirectDepositAccountInfo from './components/DirectDepositAccountInfo';
import CraDdaEnrollModal from '../Home/components/CraDdaEnrollModal';
// Import Local Layouts
import ProfileEdit from './layouts/ProfileEdit';
import ProfileShow from './layouts/ProfileShow';
import LoginCredentialsForm from './components/LoginCredentialsForm';
// Import Component Styles
import { HeadingWrapper } from './styles';

const Profile = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Define States
  // component states
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [personalInfoAlert, setPersonalInfoAlert] = useAlert();
  const [cardInfoAlert, setCardInfoAlert] = useAlert();
  const [loginCredentialsAlert, setLoginCredentialsAlert] = useAlert();

  // data states
  const [firstName, setFirstName] = useState(null);
  const [lastName, setLastName] = useState(null);
  const [cardholder, setCardholder] = useState(null);
  const [editCardholder, setEditCardholder] = useState(false);
  const [currentAccount, setCurrentAccount] = useState(null);
  const [programProcessor, setProgramProcessor] = useState(null);
  const [editLoginCredentials, setEditLoginCredentials] = useState(false);
  const [showCraDdaModal, setShowCraDdaModal] = useState(false);

  const programConfigs = useContext(ProgramConfigContext);

  // 124 = CAD; Canadian chips sync offline PIN only at an ATM
  const isCanadianProgram = programConfigs && programConfigs.currency === '124';

  // Read `=== false` rather than falsy: the column defaults to on in bps-go, so a
  // program — or an API — that does not answer leaves the address editable, as it
  // was before the flag existed.
  const addressReadOnly =
    programConfigs && programConfigs.allow_address_update === false;

  const page = 'profile';

  // Re-read the account after a PIN unlock, which can also lift the card's suspension.
  // No page loader: the card panel stays mounted and keeps what it is showing.
  const refreshCurrentAccount = async () => {
    const cardholderData = await get('/api/v1/cardholder_portal/cardholder');
    setCurrentAccount(getCurrentAccount(cardholderData.data));
    setCardholder(cardholderData.data);
  };

  // Define effects
  useEffect(() => {
    const fetchData = async () => {
      setIsError(false);
      setIsLoading(true);

      try {
        const cardholderData = await get(
          '/api/v1/cardholder_portal/cardholder'
        );

        setFirstName(cardholderData.data.first_name);
        setLastName(cardholderData.data.last_name);

        const account = getCurrentAccount(cardholderData.data);
        setCurrentAccount(account);

        setCardholder(cardholderData.data);
      } catch (error) {
        setIsError(true);
      }

      setIsLoading(false);
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const processorData = await get(
          '/api/v1/cardholder_portal/cardholder/program_processor'
        );

        setProgramProcessor(processorData.data);
      } catch (error) {
        console.log(error); //eslint-disable-line
      }
    };

    fetchData();
  }, []);

  // Galileo will redirect to cardholder portal after the direct render set pin form is completed
  // If redirected from galileo set pin direct render form, URL will have query params set_pin_state
  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const setPinState = query.get('set_pin_state');
    // Handle success/failure of Galileo hosted direct render form by displaying alert banners
    if (setPinState === 'failure') {
      // replace: drops set_pin_state from history so Back can't re-fire this effect
      navigate('/profile', {
        state: {
          cardInfoAlert: {
            message: profileEn[`${page}-failed-set-pin-alert`],
            messageFor: 'failed-set-pin',
            type: 'danger',
          },
        },
        replace: true,
      });
    } else if (setPinState === 'success') {
      // New PIN is only staged after set pin form completion. A commit is needed to complete set PIN
      // cardInfoAlert in the history state allows display of the success/failed alerts
      const commitPin = async () => {
        try {
          await post(
            `/api/v1/cardholder_portal/cardholder/accounts/${query.get(
              'account_id'
            )}/pin/commit`,
            {
              last_four_digits: query.get('last_four_digits'),
            }
          );
          const successKey = isCanadianProgram
            ? 'successful-set-pin-ca'
            : 'successful-set-pin';
          navigate('/profile', {
            state: {
              cardInfoAlert: {
                message: profileEn[`${page}-${successKey}-alert`],
                messageFor: successKey,
                type: 'success',
              },
            },
            replace: true,
          });
        } catch (e) {
          navigate('/profile', {
            state: {
              cardInfoAlert: {
                message: profileEn[`${page}-failed-set-pin-alert`],
                messageFor: 'failed-set-pin',
                type: 'danger',
              },
            },
            replace: true,
          });
        }
      };
      commitPin();
    }
    // don't add isCanadianProgram to deps: re-fires commitPin, duplicate POST /pin/commit
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, navigate]);

  useEffect(() => {
    if (location.state && location.state.cardInfoAlert) {
      setCardInfoAlert(location.state.cardInfoAlert);
    }
  }, [location.state, setCardInfoAlert]);

  if (isError) return <ErrorComponent />;
  if (isLoading) return <Loader />;

  const updateCardholderProfile = async input => {
    setIsLoading(true);

    // `phones` replaces the processor's list in full, so it is rebuilt rather than
    // echoed: every number the cardholder cannot edit is kept as-is, and the one
    // they can is written with the type they picked.
    const { phone_type: phoneType, ...rest } = input;
    const stored = Array.isArray(cardholder?.phones) ? cardholder.phones : [];
    const primary = stored.find(
      p => subscriberDigits(p.number) === subscriberDigits(cardholder?.phone)
    );
    const payload = {
      ...rest,
      phones: [
        ...stored.filter(p => p !== primary),
        { number: rest.phone, type: phoneType ?? primary?.type ?? 'mobile' },
      ],
    };

    try {
      await post('/api/v1/cardholder_portal/cardholder/update', payload);

      // Re-read rather than echo: the update responds with the bare cardholder row,
      // without name, address or phones. Its own try, because the save already
      // succeeded and a failed re-read must not be reported as a failed save.
      try {
        const refreshed = await get('/api/v1/cardholder_portal/cardholder');
        setCardholder(refreshed?.data || payload);
      } catch (refreshError) {
        setCardholder(payload);
      }

      setPersonalInfoAlert({
        message: profileEn[`${page}-profile-updated-alert`],
        messageFor: 'profile-updated',
        type: 'success',
      });
      setEditCardholder(false);
    } catch (e) {
      setPersonalInfoAlert({
        message: e.message,
        type: 'danger',
      });
    }
    setIsLoading(false);
  };

  // Define Handlers
  const handleViewChange = () => {
    setPersonalInfoAlert(null);
    setEditCardholder(!editCardholder);
  };

  const handleSubmit = input => {
    // get state keys to check input against based on the input country
    const keys = input.country === '840' ? stateKeys : provinceKeys;
    // check that the selected state is valid for the selected country. Skipped when
    // the address is read-only: the cardholder cannot have touched the state, so a
    // stored value that fails this would block every other edit with no way out.
    if (!addressReadOnly && !checkSelectedState(input, keys, input.country))
      return setPersonalInfoAlert({
        message: profileEn[`${page}-invalid-state-alert`],
        messageFor: 'invalid-state',
        type: 'danger',
      });

    return updateCardholderProfile(input);
  };

  const handleIconType = () => {
    setLoginCredentialsAlert(null);
    setEditLoginCredentials(!editLoginCredentials);
  };


  return (
    <>
      <HeadingWrapper>
        <Heading
          page="profile"
          heading="{name} Profile"
          values={{
            name: `${firstName} ${lastName}`,
          }}
        />
      </HeadingWrapper>
      <Card>
        {cardInfoAlert && (
          <Alert
            type={cardInfoAlert.type}
            alert={cardInfoAlert.message}
            page={page}
            alertFor={cardInfoAlert.messageFor}
          />
        )}
        <CardInfo
          currentAccount={currentAccount}
          programProcessor={programProcessor}
          programConfigs={programConfigs}
          onPinUnblocked={refreshCurrentAccount}
          onPinChanged={setCardInfoAlert}
        />
      </Card>
      {currentAccount.bank_details && (
        <Card>
          <DirectDepositAccountInfo
            accountInfo={currentAccount}
            onEnroll={() => setShowCraDdaModal(true)}
          />
        </Card>
      )}
      <Card>
        {personalInfoAlert && (
          <Alert
            type={personalInfoAlert.type}
            alert={personalInfoAlert.message}
            page={page}
            alertFor={personalInfoAlert.messageFor}
          />
        )}
        {editCardholder ? (
          <ProfileEdit
            onClick={handleViewChange}
            cardholder={cardholder}
            addressReadOnly={addressReadOnly}
            onSubmit={handleSubmit}
          />
        ) : (
          <ProfileShow cardholder={cardholder} onClick={handleViewChange} />
        )}
      </Card>
      <Card>
        {loginCredentialsAlert && (
          <Alert
            type={loginCredentialsAlert.type}
            alert={loginCredentialsAlert.message}
            page={page}
            alertFor={loginCredentialsAlert.messageFor}
          />
        )}
        <LoginCredentialsForm
          iconType={editLoginCredentials ? 'x' : 'edit'}
          onClick={handleIconType}
          username={cardholder.username}
        />
      </Card>
      {showCraDdaModal && (
        <CraDdaEnrollModal
          onClose={() => setShowCraDdaModal(false)}
          accountId={currentAccount.id}
          cardholder={cardholder}
          account={currentAccount}
        />
      )}
    </>
  );
};

export default Profile;
